<template>
  <div class="card-payment-form">
    <div class="payment-header">
      <h4>Paiement</h4>
      <p class="security-text">Toutes les transactions sont sécurisées et chiffrées.</p>
    </div>

    <!-- Sélecteur de mode de paiement -->
    <div class="payment-selector">
      <label class="payment-type active">
        <input type="radio" name="payment_type" value="card" checked />
        <span class="radio-custom"></span>
        <span class="payment-label">Carte de crédit</span>
        <div class="card-icons">
          <img src="/icons/visa.svg" alt="Visa" />
          <img src="/icons/mastercard.svg" alt="Mastercard" />
          <img src="/icons/amex.svg" alt="American Express" />
          <img src="/icons/discover.svg" alt="Discover" />
          <span class="more-cards">+2</span>
        </div>
      </label>
    </div>

    <!-- Formulaire de carte avec Stripe Elements -->
    <div class="card-form">
      <!-- Stripe Card Element -->
      <div class="form-group">
        <label>Informations de carte</label>
        <div id="card-element" class="stripe-element">
          <!-- Stripe Elements sera monté ici -->
        </div>
        <div id="card-errors" class="stripe-errors" v-if="cardError">
          {{ cardError }}
        </div>
      </div>

      <!-- Champ nom (non-sensible, reste en HTML) -->
      <div class="form-group">
        <label for="cardholder-name">Nom sur la carte</label>
        <input
          id="cardholder-name"
          type="text"
          v-model="formData.cardholderName"
          placeholder="Jean Dupont"
        />
      </div>

      <!-- Checkbox adresse -->
      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="useShippingAddress" />
          <span class="checkbox-custom"></span>
          <span>Utiliser l'adresse d'expédition comme adresse de facturation</span>
        </label>
      </div>

      <!-- Code postal conditionnel -->
      <div class="form-group" v-if="!useShippingAddress">
        <label for="billing-postal">Code postal de facturation</label>
        <input
          id="billing-postal"
          type="text"
          v-model="formData.billingPostalCode"
          placeholder="75001"
          maxlength="10"
        />
      </div>
    </div>
    <!-- Affichage des erreurs Stripe -->
    <div v-if="stripeError" class="stripe-error">
      <IconLibrary name="alert-circle" size="sm" />
      <span>{{ stripeError }}</span>
    </div>

    <div class="form-actions">
      <button
        type="button"
        class="btn btn-primary btn-lg"
        @click="processPayment"
        :disabled="!isFormValid || processing || !stripe"
      >
        <span v-if="processing">Traitement en cours...</span>
        <span v-else-if="!stripe">Chargement...</span>
        <span v-else>Vérifier la commande</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import IconLibrary from '../Shared/IconLibrary.vue';

const props = defineProps({
  shippingAddress: {
    type: Object,
    default: () => ({})
  }
});

const formData = ref({
  cardholderName: '',
  billingPostalCode: ''
});

const useShippingAddress = ref(true);
const processing = ref(false);

// Stripe variables
const stripe = ref(null);
const elements = ref(null);
const cardElement = ref(null);
const stripeError = ref(null);
const cardError = ref(null);

// TODO: Remplacer par votre clé publique Stripe
const STRIPE_PUBLIC_KEY =
  process.env.VUE_APP_STRIPE_PUBLIC_KEY ||
  'pk_test_51234567890abcdefghijklmnopqrstuvwxyz1234567890abcdefghijklmnopqr';

const isFormValid = computed(() => {
  const nameValid = formData.value.cardholderName.trim().length > 0;
  const cardValid = cardElement.value && !cardError.value;
  const postalValid =
    useShippingAddress.value || formData.value.billingPostalCode.trim().length > 0;

  return nameValid && cardValid && postalValid;
});

// Initialiser Stripe
onMounted(async () => {
  try {
    stripe.value = await loadStripe(STRIPE_PUBLIC_KEY);
    if (!stripe.value) {
      stripeError.value = 'Impossible de charger Stripe';
      return;
    }

    // Créer les Elements avec configuration avancée
    elements.value = stripe.value.elements({
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#6366f1', // Couleur principale (ton brand-blue)
          colorBackground: '#ffffff',
          colorText: '#424770',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          borderRadius: '8px'
        },
        rules: {
          '.Input': {
            border: '2px solid rgba(99, 102, 241, 0.2)',
            boxShadow: 'none',
            transition: 'border-color 0.2s ease'
          },
          '.Input:focus': {
            border: '2px solid #6366f1',
            boxShadow: 'none'
          },
          '.Input--invalid': {
            border: '2px solid #ff4757'
          }
        }
      },
      locale: 'fr' // Interface en français
    });

    // Créer le Card Element (clean, styling géré par Elements appearance)
    cardElement.value = elements.value.create('card', {
      // Cacher le code postal du Card Element puisqu'on le gère séparément
      hidePostalCode: true
    });

    // Monter le Card Element
    cardElement.value.mount('#card-element');

    // Écouter les changements pour la validation
    cardElement.value.on('change', (event) => {
      if (event.error) {
        cardError.value = event.error.message;
      } else {
        cardError.value = null;
      }
    });
  } catch (error) {
    console.error('Erreur lors du chargement de Stripe:', error);
    stripeError.value = 'Erreur lors du chargement de Stripe';
  }
});

const processPayment = async () => {
  if (!isFormValid.value || !stripe.value || !cardElement.value) return;

  processing.value = true;

  try {
    // Préparer l'adresse de facturation
    const billingDetails = {
      name: formData.value.cardholderName
    };

    // Ajouter l'adresse selon le choix utilisateur
    if (useShippingAddress.value && props.shippingAddress?.zip) {
      billingDetails.address = {
        postal_code: props.shippingAddress.zip,
        city: props.shippingAddress.city,
        country: props.shippingAddress.country,
        line1: props.shippingAddress.address1,
        line2: props.shippingAddress.address2
      };
    } else if (formData.value.billingPostalCode) {
      billingDetails.address = {
        postal_code: formData.value.billingPostalCode
      };
    }

    // Créer un Payment Method avec Stripe Elements
    const { error, paymentMethod } = await stripe.value.createPaymentMethod({
      type: 'card',
      card: cardElement.value,
      billing_details: billingDetails
    });

    if (error) {
      console.error('Erreur Stripe:', error);
      stripeError.value = error.message;
      processing.value = false;
      return;
    }

    console.log('Payment Method créé:', paymentMethod);

    // Émettre les données pour que le parent puisse créer la commande et traiter le paiement
    emit('process-payment', {
      type: 'card',
      paymentMethod: paymentMethod,
      billingDetails: billingDetails,
      formData: {
        cardholderName: formData.value.cardholderName,
        useShippingAddress: useShippingAddress.value
      }
    });
  } catch (error) {
    console.error('Erreur lors du paiement:', error);
    stripeError.value = 'Erreur lors du traitement du paiement';
    processing.value = false;
  }
};

const emit = defineEmits(['process-payment']);
</script>

<style scoped lang="scss">
.card-payment-form {
  background: $white;
  border-radius: $base-radius;
  padding: 1.5rem;
  border: 1px solid rgba($brand-blue, 0.1);
}

.payment-header {
  margin-bottom: 1.5rem;

  h4 {
    margin: 0 0 0.5rem 0;
    color: $brand-blue;
  }

  .security-text {
    margin: 0;
    font-size: 0.9rem;
    color: $brand-grey;
  }
}

.payment-selector {
  border: 2px solid rgba($brand-blue, 0.2);
  border-radius: $base-radius;
  margin-bottom: 1rem;

  &.alternative {
    border-color: rgba($brand-grey, 0.2);
  }
}

.payment-type {
  display: flex;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  gap: 0.75rem;

  &.active {
    background: rgba($brand-blue, 0.05);
    border-color: $brand-blue;
  }

  input[type='radio'] {
    display: none;
  }
}

.radio-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #ccc;
  border-radius: 50%;
  position: relative;

  .payment-type.active & {
    border-color: $brand-blue;
    background: $brand-blue;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      background: white;
      border-radius: 50%;
    }
  }
}

.payment-label {
  font-weight: 600;
  color: $brand-blue;
  flex: 1;
}

.card-icons {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  img {
    height: 20px;
    width: auto;
  }

  .more-cards {
    font-size: 0.8rem;
    color: $brand-grey;
    font-weight: 500;
  }
}

.card-form {
  background: rgba($brand-blue, 0.02);
  padding: 1.5rem;
  border-radius: $base-radius;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: $brand-blue;
  }

  input[type='text'] {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid rgba($brand-blue, 0.2);
    border-radius: $base-radius;
    font-size: 1rem;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: $brand-blue;
    }

    &::placeholder {
      color: #999;
    }
  }
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.input-with-icon {
  position: relative;

  .security-icon,
  .help-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: $brand-grey;
  }
}

.checkbox-group {
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;

    input[type='checkbox'] {
      display: none;
    }
  }
}

.checkbox-custom {
  width: 18px;
  height: 18px;
  border: 2px solid rgba($brand-blue, 0.3);
  border-radius: 3px;
  position: relative;

  .checkbox-label input:checked + & {
    background: $brand-blue;
    border-color: $brand-blue;

    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 12px;
      font-weight: bold;
    }
  }
}

.stripe-element {
  // Minimal styling - Stripe Elements appearance gère le reste
  padding: 0.75rem;

  background: white;

  border: 2px solid rgba($brand-blue, 0.2);
  border-radius: $base-radius;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: $brand-blue;
  }

  &::placeholder {
    color: #999;
  }
}

.stripe-errors {
  color: #ff4757;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.stripe-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(#ff4757, 0.1);
  border: 1px solid rgba(#ff4757, 0.3);
  border-radius: $base-radius;
  color: #ff4757;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.form-actions {
  margin-top: 1.5rem;

  .btn {
    width: 100%;
    padding: 0.875rem;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: $base-radius;
    cursor: pointer;
    transition: all 0.2s ease;

    &.btn-primary {
      background: linear-gradient(135deg, $brand-blue, darken($brand-blue, 10%));
      color: white;

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba($brand-blue, 0.3);
      }

      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
    }
  }
}

@media (max-width: 767px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .card-icons {
    flex-wrap: wrap;
  }

  .payment-type {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
